// =============================================================================
// TYPES
// =============================================================================

/** 2D affine transform (rotation + translation, no scale) */
export interface RigidTransform {
  /** Rotation angle in radians */
  rotation: number;
  /** Translation [tx, ty] applied after rotation */
  translation: [number, number];
}

/**
 * A vertex source: either a static coordinate or a reactive getter.
 * Static: resolved at init time, fixed coordinate.
 * Reactive: function that returns current coordinate (for elements depending on draggable points).
 */
export type VertexSource = [number, number] | (() => [number, number]);

/**
 * Result of placing a shape in world coordinates.
 */
export interface PlacedShape {
  elementId: string;
  type: string;
  /** World-space vertices (static or reactive) */
  vertices: Record<string, VertexSource>;
  /** Transform applied from canonical → world */
  transform: RigidTransform;
  /** Whether any vertex is reactive (function-based) */
  isReactive: boolean;
}

// =============================================================================
// DEFAULTS
// =============================================================================

/** Identity transform (no change) */
export const IDENTITY_TRANSFORM: RigidTransform = {
  rotation: 0,
  translation: [0, 0],
};

// =============================================================================
// UTILITIES
// =============================================================================

/**
 * Apply a rigid-body transform to a 2D point.
 */
export function applyTransform(point: [number, number], transform: RigidTransform): [number, number] {
  const cos = Math.cos(transform.rotation);
  const sin = Math.sin(transform.rotation);
  const [x, y] = point;
  return [cos * x - sin * y + transform.translation[0], sin * x + cos * y + transform.translation[1]];
}

/**
 * Compose two transforms: first apply `a`, then apply `b`.
 */
export function composeTransforms(a: RigidTransform, b: RigidTransform): RigidTransform {
  const cos = Math.cos(b.rotation);
  const sin = Math.sin(b.rotation);
  const [ax, ay] = a.translation;
  return {
    rotation: a.rotation + b.rotation,
    translation: [cos * ax - sin * ay + b.translation[0], sin * ax + cos * ay + b.translation[1]],
  };
}

/**
 * Resolve a VertexSource to its current coordinate.
 */
export function resolveVertex(source: VertexSource): [number, number] {
  return typeof source === 'function' ? source() : source;
}
